// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useEffect, useRef, useLayoutEffect, useState } from 'react';
import './BBoxImageView.scss';

/**
 * BBoxImageView
 * Renders an image inside a responsive container and overlays bounding boxes whose coordinates
 * are given in the original image coordinate space (pixels).
 *
 * Props:
 *  - image: { src: string, width: number, height: number }  // Image element or meta
 *  - boundingBoxes: { [id]: { bbox: [x1, y1, x2, y2], selected?: boolean, [key: string]: any } }
 *  - onBBoxClicked: function(id)
 *  - className, style: optional wrapper props
 */
export default function BBoxImageView({
  image,
  boundingBoxes = {},
  onBBoxClicked,
  className = '',
  style = {},
  children,
  baleImageQuality,
}) {
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [initialized, setInitialized] = useState(false);

  // Observe container size
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Recompute scale & offset whenever container or image changes
  useEffect(() => {
    if (!image || !image.width || !image.height) return;
    const { width: cw, height: ch } = containerSize;
    if (!cw || !ch) return;

    const s = Math.min(cw / image.width, ch / image.height);
    setScale(s);
    setOffset({ x: (cw - image.width * s) / 2, y: (ch - image.height * s) / 2 });
    setInitialized(true);
  }, [image, containerSize]);

  const handleBBoxClick = (id) => {
    if (onBBoxClicked) onBBoxClicked(id);
  };

  return (
    <div ref={containerRef} className={`bbox-image-view ${className}`} style={{ ...style }}>
      {image && initialized && (
        <img
          src={image.src}
          alt=''
          style={{
            position: 'absolute',
            left: `${offset.x}px`,
            top: `${offset.y}px`,
            width: `${image.width * scale}px`,
            height: `${image.height * scale}px`,
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Bounding boxes */}
      {image &&
        initialized &&
        Object.entries(boundingBoxes).map(([id, data]) => {
          if (!data?.bbox || data.bbox.length < 4) return null;
          const [x1, y1, x2, y2] = data.bbox;
          const left = offset.x + x1 * scale;
          const top = offset.y + y1 * scale;
          const width = (x2 - x1) * scale;
          const height = (y2 - y1) * scale;

          let hasQualityIssues = false;
          if (baleImageQuality?.[id] && baleImageQuality?.[id] !== 'no_issues') {
            hasQualityIssues = true;
          }

          let borderColor, backgroundColor, opacity;
          if (!data?.selected) {
            // Unselected bales (regardless of quality issues) get grey treatment
            borderColor = '#4b5563'; // Darker grey border for better visibility
            backgroundColor = 'rgba(0, 0, 0, 0.5)'; // Stronger grey filling/overlay
            opacity = 0.8;
          } else if (hasQualityIssues) {
            // Selected bales with quality issues get yellow border, no filling
            borderColor = '#f59e0b'; // Yellow for quality issues
            backgroundColor = 'transparent';
            opacity = 1;
          } else {
            // Selected bales without quality issues get green border, no filling
            borderColor = '#16a34a'; // Green for selected
            backgroundColor = 'transparent';
            opacity = 1;
          }

          return (
            <div
              key={id}
              className='bbox-image-view__bbox'
              style={{
                left,
                top,
                width,
                height,
                borderColor: borderColor,
                backgroundColor: backgroundColor,
                opacity: opacity,
                border: `3px solid ${borderColor}`,
              }}
              onClick={() => handleBBoxClick(id)}
            />
          );
        })}
      {children}
    </div>
  );
}
